import { RESOURCE_ACTIONS, type Permission, type PermissionRequirement } from '@/types/auth';
import type { AuthIdentity } from '@/types/identity';

const catalog = new Set<string>(Object.entries(RESOURCE_ACTIONS).flatMap(
  ([resource, actions]) => actions.map(action => `${resource}.${action}`),
));
const commonScopedGrants = new Set([
  'dashboard.read', 'notifications.read', 'notifications.update', 'notifications.delete',
  'customers.lookup', 'orders.read',
]);
const printingGrants = new Set([
  'services.read', 'service_lines.read', 'materials.read', 'production.read',
  'production.print', 'production.report_error', 'reports.production.read',
]);
const deliveryGrants = new Set(['delivery.read', 'delivery.progress', 'reports.delivery.read']);
const fieldDependencies: Record<string, Permission> = {
  'services.prices.read': 'services.read',
  'materials.costs.read': 'materials.read',
  'orders.financials.read': 'orders.read',
  'production.costs.read': 'production.read',
};

export function hasPermission(user: AuthIdentity | null | undefined, permission: string): boolean {
  if (!user || user.must_change_password || !user.actor_id || !catalog.has(permission) ||
      !user.permissions.includes(permission)) return false;
  if (user.scope !== 'all') {
    if (user.scope === 'printing_room') {
      if (!['Printing Room 1', 'Printing Room 2'].includes(user.printing_room)) return false;
      if (!commonScopedGrants.has(permission) && !printingGrants.has(permission)) return false;
    } else if (user.scope === 'assigned_delivery') {
      if (!commonScopedGrants.has(permission) && !deliveryGrants.has(permission)) return false;
    } else return false;
  }
  const separator = permission.lastIndexOf('.');
  const resource = permission.slice(0, separator);
  const action = permission.slice(separator + 1);
  const read = `${resource}.read`;
  if (action !== 'read' && action !== 'lookup' && catalog.has(read) && !hasPermission(user, read)) return false;
  if (permission === 'production.print' && !hasPermission(user, 'orders.read')) return false;
  const dependency = fieldDependencies[permission];
  return !dependency || hasPermission(user, dependency);
}

export function meetsRequirement(user: AuthIdentity | null | undefined, requirement: PermissionRequirement | undefined): boolean {
  if (!requirement || !(requirement.allOf?.length || requirement.anyOf?.length)) return false;
  if (requirement.allOf && (!requirement.allOf.length || !requirement.allOf.every(p => hasPermission(user, p)))) return false;
  if (requirement.anyOf && !requirement.anyOf.some(p => hasPermission(user, p))) return false;
  return true;
}
